import { FetchError } from "@medusajs/js-sdk"
import type { AuthLoginResponse } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import { UseMutationOptions, useMutation } from "@tanstack/react-query"
import { sdk } from "../../lib/client"

export const useSignInWithEmailPass = (
  options?: UseMutationOptions<
    | string
    | {
        location: string
      },
    FetchError,
    HttpTypes.AdminSignUpWithEmailPassword
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.auth.login("user", "emailpass", payload),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useSignInWithGoogle = (
  // 2.18: auth.login can also answer with MFA/verification objects, so the
  // type must carry the SDK's full union — not just the token-or-redirect
  // pair this flow actually sees.
  options?: UseMutationOptions<AuthLoginResponse, FetchError, { callback_url: string }>
) => {
  return useMutation({
    mutationFn: (payload) => sdk.auth.login("user", "google", payload),
    ...options,
  })
}

export const useRequestOtp = (
  options?: UseMutationOptions<AuthLoginResponse, FetchError, { email: string }>
) => {
  return useMutation({
    mutationFn: (payload) => sdk.auth.login("user", "otp", payload),
    ...options,
  })
}

export const useVerifyOtp = (
  options?: UseMutationOptions<
    string,
    FetchError,
    { email: string; otp_code: string }
  >
) => {
  return useMutation({
    // Raw fetch, not sdk.auth.callback: the OTP provider reads its callback
    // payload from the POST body (email/otp_code), not query params —
    // matches the same pattern already used by store-mcp-server's OTP tool.
    mutationFn: async (payload) => {
      const { token } = await sdk.client.fetch<{ token: string }>(
        "/auth/user/otp/callback",
        { method: "POST", body: payload }
      )
      return token
    },
    ...options,
  })
}

/**
 * Binds an already-authenticated admin's account to a Google identity.
 * Admin accounts never auto-link (see the backend auth-google provider) —
 * this is the explicit opt-in half: call it with the unlinked token
 * returned by the /auth/user/google callback while the admin is still
 * signed in with their own session.
 */
export const useLinkGoogleAccount = (
  options?: UseMutationOptions<
    { linked: boolean },
    FetchError,
    { google_token: string }
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.client.fetch<{ linked: boolean }>("/admin/auth/link-google", {
        method: "POST",
        body: payload,
      }),
    ...options,
  })
}

export const useSignUpWithEmailPass = (
  options?: UseMutationOptions<
    string,
    FetchError,
    HttpTypes.AdminSignInWithEmailPassword
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.auth.register("user", "emailpass", payload),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useResetPasswordForEmailPass = (
  options?: UseMutationOptions<void, FetchError, { email: string }>
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.auth.resetPassword("user", "emailpass", {
        identifier: payload.email,
      }),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}

export const useLogout = (options?: UseMutationOptions<void, FetchError>) => {
  return useMutation({
    mutationFn: () => sdk.auth.logout(),
    ...options,
  })
}

export const useUpdateProviderForEmailPass = (
  token: string,
  options?: UseMutationOptions<void, FetchError, HttpTypes.AdminUpdateProvider>
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.auth.updateProvider("user", "emailpass", payload, token),
    onSuccess: async (data, variables, context) => {
      options?.onSuccess?.(data, variables, context)
    },
    ...options,
  })
}
